import React from 'react';
import LoadingSpinner from '../ui/LoadingSpinner';

interface DiversificationData {
  name: string;
  value: number;
  isOthers?: boolean;
}

interface TypeSpecificDiversificationSectionProps {
  investmentType: 'total' | 'etfs' | 'assets';
  top5IndustryData: DiversificationData[];
  top5CountryData: DiversificationData[];
  isLoading: boolean;
}

const subtitleMap = {
  total: 'combined, look-through',
  etfs: 'ETF look-through',
  assets: 'direct holdings',
};

const BarList: React.FC<{ data: DiversificationData[] }> = ({ data }) => {
  const max = data[0]?.value ?? 1;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {data.map(item => {
        const pct = (item.value * 100).toFixed(1);
        const barWidth = max > 0 ? (item.value / max) * 100 : 0;
        return (
          <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{
              width: '160px',
              flexShrink: 0,
              fontSize: '0.82rem',
              color: '#52525b',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {item.name}
            </span>
            <div style={{ flex: 1, height: '6px', background: '#f4f4f5', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{
                width: `${barWidth}%`,
                height: '100%',
                background: '#000537',
                borderRadius: '3px',
                transition: 'width 0.4s ease',
              }} />
            </div>
            <span style={{
              width: '48px',
              flexShrink: 0,
              textAlign: 'right',
              fontSize: '0.82rem',
              color: '#52525b',
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}>
              {pct}%
            </span>
          </div>
        );
      })}
    </div>
  );
};

const BreakdownCard: React.FC<{
  title: string;
  subtitle: string;
  data: DiversificationData[];
  isLoading: boolean;
  emptyText: string;
}> = ({ title, subtitle, data, isLoading, emptyText }) => (
  <div className="card card-forest h-100">
    <div className="card-body p-3 p-md-4">
      <div className="d-flex justify-content-between align-items-baseline mb-4">
        <span className="fw-semibold" style={{ fontSize: '0.95rem', color: '#000537' }}>{title}</span>
        <span style={{ fontSize: '0.75rem', color: '#a1a1aa' }}>{subtitle}</span>
      </div>
      {isLoading ? (
        <div className="d-flex align-items-center justify-content-center py-4">
          <LoadingSpinner size="sm" text="Loading..." />
        </div>
      ) : data.length > 0 ? (
        <BarList data={data} />
      ) : (
        <div className="d-flex align-items-center justify-content-center py-4" style={{ color: '#a1a1aa', fontSize: '0.875rem' }}>
          {emptyText}
        </div>
      )}
    </div>
  </div>
);

const TypeSpecificDiversificationSection: React.FC<TypeSpecificDiversificationSectionProps> = ({
  investmentType,
  top5IndustryData,
  top5CountryData,
  isLoading,
}) => {
  const subtitle = subtitleMap[investmentType];

  return (
    <div className="row g-3 mb-4">
      <div className="col-12 col-lg-6">
        <BreakdownCard
          title="Sector "
          subtitle={subtitle}
          data={top5IndustryData}
          isLoading={isLoading}
          emptyText="No industry data available"
        />
      </div>
      <div className="col-12 col-lg-6">
        <BreakdownCard
          title="Region"
          subtitle={subtitle}
          data={top5CountryData}
          isLoading={isLoading}
          emptyText="No country data available"
        />
      </div>
    </div>
  );
};

export default TypeSpecificDiversificationSection;
